import { ctaLineHeight, ctaLineHeightDesktop, semanticColors } from "../../tokens";
import type { SpinnerColor, SpinnerProps, SpinnerSize } from "./types";

/** The defaults both platform files read */
export const spinnerDefaults = {
  size: "large",
  color: "secondary",
  accessibilityLabel: "Loading",
  decorative: false,
} as const satisfies Required<
  Pick<SpinnerProps, "size" | "color" | "accessibilityLabel" | "decorative">
>;

// Figma binds each size to `_cta/line-height` sm/md/lg: 12/16/24, desktop 16/24/28
export const sizeMap: Record<SpinnerSize, { mobile: number; desktop: number }> = {
  large: { mobile: ctaLineHeight.lg, desktop: ctaLineHeightDesktop.lg },
  medium: { mobile: ctaLineHeight.md, desktop: ctaLineHeightDesktop.md },
  small: { mobile: ctaLineHeight.sm, desktop: ctaLineHeightDesktop.sm },
};

// Figma draws one stroke per size and binds no variable (split row 218)
export const strokeMap: Record<SpinnerSize, { mobile: number; desktop: number }> = {
  large: { mobile: 3, desktop: 3 },
  medium: { mobile: 2, desktop: 2.5 },
  small: { mobile: 1.5, desktop: 2 },
};

export const colorMap: Record<SpinnerColor, string> = {
  primary: semanticColors.neutral.foreground.inverse,
  secondary: semanticColors.brand.onPrimarySurface,
  destructive: semanticColors.system.danger.default,
  success: semanticColors.system.success.default,
  // A Neutral button's loading arc (Figma's Spinner set doesn't draw it)
  neutral: semanticColors.neutral.foreground.default,
  // A disabled button's loading arc: the shared disabled grey (Figma draws no
  // disabled-and-loading cell)
  disabled: semanticColors.button.disabled.onCtaDisabled,
};

export const trailColor: string = semanticColors.overlay[20];

/**
 * One turn of the spinner. Invented here, not a motion token: there are none
 * yet (standards register row 6, ADR-021), so both platforms read this one.
 */
export const SPINNER_ROTATION_MS = 750;

/** The circle both platforms draw: its radius, and the quarter-turn arc. */
export function getSpinnerArc(size: number, strokeWidth: number) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  return { radius, circumference, arcLength: circumference * 0.25 };
}
